// Auto-Apply API client for Career Assistant v5
import { apiClient } from '../api-client';
import type { 
  AutoApplyRule,
  AutoApplyRulesResponse,
  AutoApplyRuleCreateRequest,
  AutoApplyRuleUpdateRequest,
  AutoApplyExecution,
  AutoApplyDashboardStats,
  AutoApplyJobBoard,
  AutoApplyLogEntry,
  JobBoardType
} from '../../types/auto-apply';

export const autoApplyApi = {
  /**
   * Get auto-apply dashboard statistics
   */
  getDashboardStats: async (): Promise<AutoApplyDashboardStats> => {
    const response = await apiClient.get('/auto-apply/dashboard');
    return response.data;
  },

  /**
   * Get auto-apply rules with filtering and pagination
   */
  getRules: async (options: {
    page?: number;
    pageSize?: number;
    status?: string;
    search?: string;
  } = {}): Promise<AutoApplyRulesResponse> => {
    const params = {
      page: options.page || 1,
      pageSize: options.pageSize || 20,
      ...(options.status && { status: options.status }),
      ...(options.search && { search: options.search }),
    };

    const response = await apiClient.get('/auto-apply/rules', { params });
    return response.data;
  },

  /**
   * Get a specific auto-apply rule by ID
   */
  getRule: async (ruleId: string): Promise<AutoApplyRule> => {
    const response = await apiClient.get(`/auto-apply/rules/${ruleId}`);
    return response.data;
  },

  /**
   * Create a new auto-apply rule
   */
  createRule: async (request: AutoApplyRuleCreateRequest): Promise<AutoApplyRule> => {
    const response = await apiClient.post('/auto-apply/rules', request);
    return response.data;
  },

  /**
   * Update an existing auto-apply rule
   */
  updateRule: async (ruleId: string, request: AutoApplyRuleUpdateRequest): Promise<AutoApplyRule> => {
    const response = await apiClient.patch(`/auto-apply/rules/${ruleId}`, request);
    return response.data;
  },

  /**
   * Delete an auto-apply rule
   */
  deleteRule: async (ruleId: string): Promise<void> => {
    await apiClient.delete(`/auto-apply/rules/${ruleId}`);
  },

  /**
   * Copy/duplicate an auto-apply rule
   */
  copyRule: async (ruleId: string): Promise<AutoApplyRule> => {
    const response = await apiClient.post(`/auto-apply/rules/${ruleId}/copy`);
    return response.data;
  },

  /**
   * Toggle rule status (active/paused)
   */
  toggleRuleStatus: async (ruleId: string): Promise<AutoApplyRule> => {
    const response = await apiClient.post(`/auto-apply/rules/${ruleId}/toggle`);
    return response.data;
  },

  /**
   * Test run a rule (dry run without actually applying)
   */
  testRule: async (ruleId: string): Promise<AutoApplyExecution> => {
    const response = await apiClient.post(`/auto-apply/rules/${ruleId}/test`);
    return response.data;
  },

  /**
   * Manually execute a rule immediately
   */
  executeRule: async (ruleId: string): Promise<AutoApplyExecution> => {
    const response = await apiClient.post(`/auto-apply/rules/${ruleId}/execute`);
    return response.data;
  },

  /**
   * Get execution history for a rule
   */
  getRuleExecutions: async (ruleId: string, options: {
    page?: number;
    pageSize?: number;
  } = {}): Promise<{ executions: AutoApplyExecution[]; total_count: number }> => {
    const params = {
      page: options.page || 1,
      pageSize: options.pageSize || 20,
    };

    const response = await apiClient.get(`/auto-apply/rules/${ruleId}/executions`, { params });
    return response.data;
  },

  /**
   * Get a specific execution by ID
   */
  getExecution: async (executionId: string): Promise<AutoApplyExecution> => {
    const response = await apiClient.get(`/auto-apply/executions/${executionId}`);
    return response.data;
  },

  /**
   * Cancel a running execution
   */
  cancelExecution: async (executionId: string): Promise<void> => {
    await apiClient.post(`/auto-apply/executions/${executionId}/cancel`);
  },

  /**
   * Get execution logs
   */
  getExecutionLogs: async (executionId: string): Promise<AutoApplyLogEntry[]> => {
    const response = await apiClient.get(`/auto-apply/executions/${executionId}/logs`);
    return response.data;
  },

  /**
   * Get available job boards
   */
  getAvailableJobBoards: async (): Promise<Array<{
    type: JobBoardType;
    name: string;
    description: string;
    features: string[];
    setup_required: boolean;
    supported: boolean;
  }>> => {
    const response = await apiClient.get('/auto-apply/job-boards');
    return response.data;
  },

  /**
   * Get job board configuration for a rule
   */
  getJobBoardConfig: async (ruleId: string, boardType: JobBoardType): Promise<AutoApplyJobBoard> => {
    const response = await apiClient.get(`/auto-apply/rules/${ruleId}/job-boards/${boardType}`);
    return response.data;
  },

  /**
   * Update job board configuration
   */
  updateJobBoardConfig: async (ruleId: string, boardType: JobBoardType, config: {
    enabled?: boolean;
    settings?: AutoApplyJobBoard['settings'];
  }): Promise<AutoApplyJobBoard> => {
    const response = await apiClient.patch(`/auto-apply/rules/${ruleId}/job-boards/${boardType}`, config);
    return response.data;
  },

  /**
   * Test job board authentication
   */
  testJobBoardAuth: async (ruleId: string, boardType: JobBoardType): Promise<{
    status: 'valid' | 'invalid' | 'expired';
    message: string;
  }> => {
    const response = await apiClient.post(`/auto-apply/rules/${ruleId}/job-boards/${boardType}/test-auth`);
    return response.data;
  },

  /**
   * Store job board credentials (encrypted)
   */
  storeJobBoardCredentials: async (ruleId: string, boardType: JobBoardType, credentials: {
    username?: string;
    password?: string;
    api_key?: string;
    [key: string]: any;
  }): Promise<void> => {
    await apiClient.post(`/auto-apply/rules/${ruleId}/job-boards/${boardType}/credentials`, {
      credentials: credentials,
    });
  },

  /**
   * Remove job board credentials
   */
  removeJobBoardCredentials: async (ruleId: string, boardType: JobBoardType): Promise<void> => {
    await apiClient.delete(`/auto-apply/rules/${ruleId}/job-boards/${boardType}/credentials`);
  },

  /**
   * Get rule performance analytics
   */
  getRuleAnalytics: async (ruleId: string, timeframe: '7d' | '30d' | '90d' = '30d'): Promise<{
    applications_over_time: Array<{ date: string; count: number }>;
    success_rate_trend: Array<{ date: string; success_rate: number }>;
    job_board_performance: Array<{ board_type: JobBoardType; applications: number; success_rate: number }>;
    top_keywords: Array<{ keyword: string; applications: number }>;
    top_companies: Array<{ company: string; applications: number }>;
  }> => {
    const response = await apiClient.get(`/auto-apply/rules/${ruleId}/analytics`, {
      params: { timeframe }
    });
    return response.data;
  },

  /**
   * Get global auto-apply analytics
   */
  getGlobalAnalytics: async (timeframe: '7d' | '30d' | '90d' = '30d'): Promise<{
    total_applications: number;
    success_rate: number;
    most_active_rules: Array<{ rule_name: string; applications: number }>;
    job_board_usage: Array<{ board_type: JobBoardType; usage_count: number }>;
    applications_by_day: Array<{ date: string; count: number }>;
  }> => {
    const response = await apiClient.get('/auto-apply/analytics', {
      params: { timeframe }
    });
    return response.data;
  },
};